import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

export interface TrackImage {
  id: string;
  track_type: string;
  title: string;
  description: string;
  storage_path: string;
  is_featured: boolean;
  is_event_flyer: boolean;
  display_order: number;
  event_dates?: EventDate[];
  url?: string;
  event?: Event; // Link to created event
}

export interface EventDate {
  id: string;
  event_date: string;
}

export interface Event {
  id: string;
  track_image_id: string;
  name: string;
  description: string;
  event_date: string;
  registration_deadline: string;
  is_active: boolean;
  default_registration_fee?: number;
  ticket_shop_url?: string;
}

export interface GlobalCategory {
  id: string;
  name: string;
  vehicle_type: 'car' | 'bike';
  default_registration_fee: number;
  default_first_place_prize: number;
  default_second_place_prize: number;
}

export const useEventManagement = () => {
  const [trackImages, setTrackImages] = useState<TrackImage[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [globalCategories, setGlobalCategories] = useState<GlobalCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrackImages();
    fetchEvents();
    fetchGlobalCategories();
  }, []);

  const fetchTrackImages = async () => {
    try {
      const { data, error } = await supabase
        .from('track_images')
        .select(`
          *,
          event_dates (
            id,
            event_date
          ),
          event:events!track_image_id (
            id,
            name,
            event_date,
            is_active,
            default_registration_fee,
            ticket_shop_url
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const imagesWithUrls = await Promise.all(
        (data || []).map(async (image) => {
          const { data: { publicUrl } } = supabase
            .storage
            .from('track-images')
            .getPublicUrl(image.storage_path);
          
          return {
            ...image,
            url: publicUrl,
            event: image.event?.[0] || null // Get first event if exists
          };
        })
      );

      setTrackImages(imagesWithUrls);
    } catch (error) {
      console.error('Error fetching track images:', error);
      toast.error('Failed to load track images');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('event_date', { ascending: false });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const fetchGlobalCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('global_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      setGlobalCategories(data || []);
    } catch (error) {
      console.error('Error fetching global categories:', error);
    }
  };

  const fetchExistingEventCategories = async (trackImageId: string) => {
    try {
      const { data, error } = await supabase
        .from('event_categories')
        .select('global_category_id')
        .in('event_id', 
          events
            .filter(e => e.track_image_id === trackImageId)
            .map(e => e.id)
        );

      if (error) throw error;
      
      // Return array of global category IDs that are already used
      return data?.map(cat => cat.global_category_id).filter(Boolean) || [];
    } catch (error) {
      console.error('Error fetching existing categories:', error);
      return [];
    }
  };

  const handleDeleteTrackImage = async (image: TrackImage) => {
    const hasEvents = hasExistingEvent(image);
    const confirmMessage = hasEvents 
      ? `Are you sure you want to delete this event? This will permanently remove the event flyer, all event dates, categories, and registrations. This action cannot be undone.`
      : `Are you sure you want to delete this image?`;
    
    if (!confirm(confirmMessage)) return;

    try {
      // Delete from storage first
      const { error: storageError } = await supabase.storage
        .from('track-images')
        .remove([image.storage_path]);

      if (storageError) throw storageError;

      // Delete from database (this will cascade delete all related data)
      const { error: dbError } = await supabase
        .from('track_images')
        .delete()
        .eq('id', image.id);

      if (dbError) throw dbError;

      const successMessage = hasEvents 
        ? 'Event and all related data deleted successfully'
        : 'Image deleted successfully';
      
      toast.success(successMessage);
      fetchTrackImages();
      fetchEvents(); // Refresh events list
    } catch (error) {
      console.error('Error deleting:', error);
      const errorMessage = hasEvents 
        ? 'Failed to delete event'
        : 'Failed to delete image';
      toast.error(errorMessage);
    }
  };

  // Helper function to format date consistently (YYYY-MM-DD format)
  const formatDateForDatabase = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00'); // Force local timezone interpretation
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  };

  // Helper function to format date for display
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return '';
    // Parse as local date to avoid timezone conversion
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const hasExistingEvent = (flyer: TrackImage) => {
    return events.some(event => event.track_image_id === flyer.id);
  };

  return {
    trackImages,
    events,
    globalCategories,
    loading,
    fetchTrackImages,
    fetchEvents,
    fetchGlobalCategories,
    fetchExistingEventCategories,
    handleDeleteTrackImage,
    formatDateForDatabase,
    formatDateForDisplay,
    hasExistingEvent
  };
};